import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, ArrowRight } from "lucide-react";
import React from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";

import FormInput from "@/components/form-fields/form-input";
import AvatarText from "@/components/reusables/AvatarText";
import { CustomToolTip } from "@/components/reusables/CustomToolTip";
import FetchWrapper from "@/components/reusables/FetchWrapper";
import FormDialog from "@/components/reusables/FormDialog";
import {
  FormSelectSingle,
  SelectMulti,
} from "@/components/reusables/custom-form-fields/FormSelect";
import EmptyData from "@/components/state-pages/EmptyData";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form } from "@/components/ui/form";
import { ScrollArea } from "@/components/ui/scroll-area";
import * as Table from "@/components/ui/table";
import { Button_Names } from "@/constants/global-constants";
import { ToolsConstants } from "@/constants/tools.constant";
import useGetFilter from "@/hooks/useGetFilter";
import { LabelValue } from "@/interfaces/interfaces";
import {
  ProductType,
  ProjectAssignToolsStatus,
} from "@/interfaces/tools.interface";
import { cn } from "@/lib/utils";
import {
  projectApi,
  useGetAllProductAssignmentQuery,
} from "@/redux/api/projectsQuery";
import { useTransferToolsMutation } from "@/redux/api/toolsQuery";
import {
  TCreateTransfer,
  createTransferToolSchema,
  defaultTransferTools,
} from "@/schemas/tools.schema";
import { customToast } from "@/utils/custom-toast";
import { validateResponse } from "@/utils/helper-functions/form.helper";
import { getList } from "@/utils/helpers";

interface TransferToolsModalProps {
  isOpen: boolean;
  toggleModal: () => void;
}

const TransferToolsModal = ({
  isOpen,
  toggleModal,
}: TransferToolsModalProps): JSX.Element => {
  const { TRANSFER_TOOLS_FORM } = ToolsConstants.REQUEST.TRANSFER_REQUEST;
  const { projectList } = useGetFilter(["project"]);
  const [transferTools, { isLoading: isToolsCreating }] =
    useTransferToolsMutation();
  const dispatch = useDispatch();

  const form = useForm<TCreateTransfer>({
    resolver: zodResolver(createTransferToolSchema),
    defaultValues: defaultTransferTools,
  });
  const { setError } = form;
  const { projectFromId } = form.watch();

  const { data: tools, ...rest } = useGetAllProductAssignmentQuery(
    {
      projectId: projectFromId,
      status: ProjectAssignToolsStatus.ONSITE,
    },
    {
      skip: !projectFromId,
    },
  );
  const toolsAvailableForTransfer = tools?.data;

  const CustomTableHeader = (): JSX.Element => {
    const commonClasses =
      "capitalize  border-r border-r-white text-primary dark:text-white";

    const isAllSelected = form
      .getValues("products")
      .every((item) => item.isSelected);

    return (
      <Table.TableHeader className={"bg-[#E4E8F2] dark:bg-primaryLight "}>
        <Table.TableRow>
          <Table.TableHead
            className={cn(commonClasses, "border-r-transparent")}
          >
            <Checkbox
              checked={isAllSelected}
              onCheckedChange={(value) => {
                form.setValue(
                  "products",
                  form.watch("products").map((item) => ({
                    ...item,
                    isSelected: Boolean(value),
                  })),
                );
              }}
              className=' mt-1'
            />
          </Table.TableHead>
          <Table.TableHead className={cn(commonClasses)}>Name</Table.TableHead>
          <Table.TableHead className={commonClasses}>Quantity</Table.TableHead>
          <Table.TableHead className={commonClasses}>
            Sub Products
          </Table.TableHead>
          <Table.TableHead className={commonClasses}>Notes</Table.TableHead>
          <Table.TableHead className={commonClasses}>Comments</Table.TableHead>
        </Table.TableRow>
      </Table.TableHeader>
    );
  };

  const { fields: availableProducts } = useFieldArray({
    control: form.control,
    name: "products",
  });

  const onSubmit = async (data: TCreateTransfer): Promise<void> => {
    let hasError = false;

    const updatedProducts = data.products.map((product, idx) => {
      if (!product.isSelected) {
        return product;
      }

      const tool = toolsAvailableForTransfer?.find(
        (t) => t.id === product.productAssignmentId,
      );

      if (
        tool &&
        tool.product.type === ProductType.Has_Quantity &&
        product.quantity > tool.assignedQuantity
      ) {
        setError(`products.${idx}.quantity`, {
          type: "manual",
          message: `Quantity cannot be greater than ${tool.assignedQuantity}`,
        });
        hasError = true;
      }

      if (
        tool &&
        tool.product.type === ProductType.Has_Quantity &&
        product.quantity < tool.assignedQuantity
      ) {
        return { ...product, isAltered: true };
      }

      return product;
    });

    if (hasError) {
      return;
    }

    const response = await transferTools({
      data: {
        ...data,
        products: updatedProducts.filter((product) => product.isSelected),
      },
    });

    validateResponse({
      response,
      onSuccess: () => {
        customToast.success(
          TRANSFER_TOOLS_FORM.TOAST_MESSAGES.TRANSFER_TOOLS_SUCCESS,
        );
        dispatch(projectApi.util.invalidateTags(["assignTools"]));
        toggleModal();
      },
    });
  };

  React.useMemo(() => {
    if (!toolsAvailableForTransfer) {
      return;
    }
    form.setValue(
      "products",
      toolsAvailableForTransfer?.length
        ? toolsAvailableForTransfer?.map((tool) => ({
            isSelected: false,
            productId: tool?.productId,
            productAssignmentId: tool?.id,
            subProductAssignmentIds:
              tool?.subProductAssignments?.map((subProduct) => subProduct.id) ||
              [],
            quantity:
              tool.product.type === ProductType.Has_Quantity
                ? tool?.assignedQuantity
                : tool.subProductAssignments?.length || 0,
            description: tool?.description ?? undefined,
            note: tool?.description ?? undefined,
            subProductIds:
              tool?.subProductAssignments?.map(
                (subProduct) => subProduct.subProduct.id,
              ) || [],
            type: tool?.product?.type,
            isAltered: false,
            prevSubProductIds:
              tool?.subProductAssignments?.map((subProduct) => subProduct.id) ||
              [],
          }))
        : [],
    );
  }, [toolsAvailableForTransfer, form]);

  return (
    <FormDialog
      title={TRANSFER_TOOLS_FORM.TITLE}
      isOpen={isOpen}
      toggleModal={toggleModal}
      className='md:max-w-7xl'
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <section className='space-y-5'>
            <section className='flex items-center gap-2 w-9/12'>
              <div className=' border border-input rounded-md p-3 w-full'>
                <FormInput
                  name='projectFromId'
                  form={form}
                  hideError
                  required
                  label='Project (From)'
                  render={(field) => (
                    <FormSelectSingle
                      placeholder='Select Project'
                      field={field}
                      options={getList(projectList)}
                    />
                  )}
                />
              </div>
              <div>
                <ArrowRight className='h-6 w-6' />
              </div>
              <div className=' border border-input rounded-md p-3 w-full'>
                <FormInput
                  name='projectToId'
                  form={form}
                  required
                  label='Project (To)'
                  render={(field) => (
                    <FormSelectSingle
                      placeholder='Select Project'
                      field={field}
                      disabled={!projectFromId}
                      options={getList(
                        projectList?.filter(
                          (item) => item.id !== projectFromId,
                        ),
                      )}
                    />
                  )}
                />
              </div>
            </section>
            <section>
              <ScrollArea className='h-[400px] sidebar_scrollbar'>
                <div className='rounded-md border overflow-clip'>
                  <FetchWrapper {...rest} isTable>
                    <Table.Table>
                      <CustomTableHeader />
                      <Table.TableBody>
                        {availableProducts?.length ? (
                          availableProducts?.map((field, index) => {
                            const commonCellClassName =
                              "border-r border-r-gray-100";
                            const isRowSelected = form.watch(
                              `products.${index}.isSelected`,
                            );
                            const product = toolsAvailableForTransfer?.find(
                              (tool) => tool?.id === field?.productAssignmentId,
                            );
                            const onValueChange = (
                              values: LabelValue[],
                            ): void => {
                              const selectedIds = values.map(
                                (value) => value.value,
                              );
                              form.setValue(
                                `products.${index}.isAltered`,
                                selectedIds.length !==
                                  product?.subProductAssignments.length,
                                {
                                  shouldDirty: true,
                                },
                              );
                              form.setValue(
                                `products.${index}.subProductIds`,
                                selectedIds,
                                {
                                  shouldDirty: true,
                                },
                              );

                              form.setValue(
                                `products.${index}.subProductAssignmentIds`,

                                product?.subProductAssignments
                                  .filter((subProduct) =>
                                    selectedIds.includes(
                                      subProduct.subProduct.id,
                                    ),
                                  )
                                  .map((subProduct) => subProduct.id),

                                {
                                  shouldDirty: true,
                                },
                              );
                            };
                            const multiSelectOptions = product
                              ?.subProductAssignments?.length
                              ? product?.subProductAssignments?.map(
                                  (subProduct) => ({
                                    label: subProduct.subProduct.name,
                                    value: subProduct.subProduct.id,
                                    customNode: subProduct?.subProduct
                                      .consumed ? (
                                      <CustomToolTip
                                        toolTipTrigger={
                                          <AlertCircle className='h-4 w-4 text-red-500' />
                                        }
                                        toolTipContent={
                                          ToolsConstants.PRODUCTS.ITEMS_CONSUMED
                                        }
                                      />
                                    ) : null,
                                  }),
                                )
                              : [];

                            return (
                              <Table.TableRow
                                key={field.id}
                                className='even:bg-transparent hover:bg-transparent'
                              >
                                <Table.TableCell>
                                  <Checkbox
                                    checked={form.getValues(
                                      `products.${index}.isSelected`,
                                    )}
                                    onCheckedChange={(value) => {
                                      form.setValue(
                                        `products.${index}.isSelected`,
                                        Boolean(value),
                                      );
                                    }}
                                  />
                                </Table.TableCell>
                                <Table.TableCell
                                  className={cn(commonCellClassName)}
                                >
                                  <AvatarText
                                    src={product?.product?.images[0]}
                                    name={product?.product?.name || ""}
                                    title={product?.product?.name || ""}
                                  />
                                </Table.TableCell>
                                <Table.TableCell
                                  className={cn(commonCellClassName)}
                                >
                                  <FormInput
                                    name={`products.${index}.quantity`}
                                    form={form}
                                    input={{
                                      placeholder: "Enter quantity",
                                      type: "number",
                                      disabled: !isRowSelected,
                                    }}
                                  />
                                </Table.TableCell>
                                <Table.TableCell
                                  className={cn(
                                    commonCellClassName,
                                    "min-w-72 max-w-72",
                                  )}
                                >
                                  {product?.product?.type ===
                                  ProductType.Has_Quantity ? (
                                    <div className='pl-3 '>--</div>
                                  ) : (
                                    <SelectMulti
                                      disabled={!isRowSelected}
                                      className='mt-2'
                                      maxSelection={
                                        form.watch(
                                          `products.${index}.quantity`,
                                        ) ?? 0
                                      }
                                      withoutForm={{
                                        values:
                                          form.getValues(
                                            `products.${index}.subProductIds`,
                                          ) ?? [],
                                        onValueChange,
                                      }}
                                      options={multiSelectOptions}
                                      isRequired
                                    />
                                  )}
                                </Table.TableCell>
                                <Table.TableCell
                                  className={cn(commonCellClassName)}
                                >
                                  <FormInput
                                    name={`products.${index}.note`}
                                    hideError
                                    form={form}
                                    input={{
                                      placeholder: "Enter notes",
                                      disabled: !isRowSelected,
                                    }}
                                  />
                                </Table.TableCell>
                                <Table.TableCell
                                  className={cn(commonCellClassName)}
                                >
                                  <FormInput
                                    name={`products.${index}.description`}
                                    hideError
                                    form={form}
                                    input={{
                                      placeholder: "Enter comments",
                                      disabled: !isRowSelected,
                                    }}
                                  />
                                </Table.TableCell>
                              </Table.TableRow>
                            );
                          })
                        ) : (
                          <Table.TableRow>
                            <Table.TableCell colSpan={7}>
                              <EmptyData
                                title='Tools'
                                subtitle='No tools available to assign'
                              />
                            </Table.TableCell>
                          </Table.TableRow>
                        )}
                      </Table.TableBody>
                    </Table.Table>
                  </FetchWrapper>
                </div>
              </ScrollArea>
            </section>
          </section>
          <div className=' flex justify-between'>
            <Button
              variant={"ghost"}
              disabled={isToolsCreating}
              onClick={() => {
                form.reset();
              }}
            >
              {Button_Names.Reset}
            </Button>
            <Button
              type='submit'
              disabled={
                !form.getValues().products?.some((item) => item.isSelected)
              }
              isLoading={isToolsCreating}
            >
              {TRANSFER_TOOLS_FORM.ADD.label}
            </Button>
          </div>
        </form>
      </Form>
    </FormDialog>
  );
};

export default TransferToolsModal;
